import { jwtDecode } from 'jwt-decode';
import Cookies from 'js-cookie';

// Cookieのオプション設定
const COOKIE_OPTIONS = {
  secure: process.env.NODE_ENV === 'production',
  sameSite: (process.env.NODE_ENV === 'production' ? 'strict' : 'lax') as 'strict' | 'lax', // 開発環境ではlaxで柔軟に
  path: '/',
  // アクセストークン: 1時間、リフレッシュトークン: 4日
  expires: 4 // 4日後に期限切れ（hub.cocorograph.comと統一）
};

// トークンの保存キー
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

/**
 * アクセストークンを取得
 */
export const getAccessToken = (): string | undefined => {
  return Cookies.get(ACCESS_TOKEN_KEY);
};

/**
 * リフレッシュトークンを取得
 */
export const getRefreshToken = (): string | undefined => {
  return Cookies.get(REFRESH_TOKEN_KEY);
};

/**
 * トークンを保存
 */
export const storeTokens = (accessToken: string, refreshToken: string): void => {
  // アクセストークンは1時間後に期限切れ
  Cookies.set(ACCESS_TOKEN_KEY, accessToken, {
    ...COOKIE_OPTIONS,
    expires: 1/24 // 1時間
  });
  // リフレッシュトークンは4日後に期限切れ（バックエンドのREFRESH_TOKEN_LIFETIMEと統一）
  Cookies.set(REFRESH_TOKEN_KEY, refreshToken, {
    ...COOKIE_OPTIONS,
    expires: 4 // 4日
  });
};

/**
 * トークンを削除
 */
export const removeTokens = (): void => {
  Cookies.remove(ACCESS_TOKEN_KEY, { path: '/' });
  Cookies.remove(REFRESH_TOKEN_KEY, { path: '/' });
};

/**
 * トークンの有効期限を確認
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<{ exp: number }>(token);
    if (!decoded.exp) return true;

    const now = Date.now();
    const expiry = decoded.exp * 1000;

    // 有効期限の2分前から期限切れとみなす（リフレッシュ失敗時の緩衝）
    const bufferTime = 2 * 60 * 1000; // 2分

    return now >= (expiry - bufferTime);
  } catch {
    return true;
  }
};

/**
 * トークンの検証
 */
export const validateToken = (token?: string) => {
  if (!token) {
    return { isValid: false, payload: null };
  }

  try {
    const payload = jwtDecode<{
      exp: number;
      user_id: number;
      username: string;
      is_staff: boolean;
    }>(token);

    return {
      isValid: !isTokenExpired(token),
      payload
    };
  } catch {
    return { isValid: false, payload: null };
  }
};

/**
 * アクセストークンの更新が必要かどうかを判定
 */
export const shouldRefreshToken = (): boolean => {
  const accessToken = getAccessToken();
  return !accessToken || isTokenExpired(accessToken);
};

/**
 * 全てのトークンが無効かどうかを判定
 */
export const areAllTokensInvalid = (): boolean => {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  return (!accessToken || isTokenExpired(accessToken)) && (!refreshToken || isTokenExpired(refreshToken));
};

/**
 * トークンリフレッシュAPIを呼び出す
 */
export const refreshTokenAPI = async (refreshToken: string): Promise<{ access: string; refresh: string }> => {
  // APIのインポートは循環参照を避けるため動的インポート
  const { default: axios } = await import('axios');
  
  const API_URL = process.env.NEXT_PUBLIC_API_URL;
  
  const response = await axios.post<{ access: string; refresh: string }>(
    `${API_URL}/token/refresh/`,
    { refresh: refreshToken },
    {
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 10000
    }
  );

  return response.data;
};
